import { useState, useMemo } from 'react';
import { palette } from '../../styles/tokens';
import { SlidePanel } from '../../components/common/SlidePanel';
import {
  TeamMemberPicker,
  type TeamMember,
  type TeamMemberStatus,
} from '../../components/common/TeamMemberPicker';
import { PRIORITY_STYLES } from './config';
import type { DispatchJob, CrewMember } from './hooks';

interface Props {
  job: DispatchJob | null;
  crew: CrewMember[];
  onAssign: (jobId: string, userId: string) => Promise<boolean>;
  onClose: () => void;
}

// Map Dispatch's finer-grained crewStatus into TeamMemberPicker's coarse
// 'available' | 'on_job' | 'off' bucket. Decided here, in the consumer,
// per the picker's "callers map their domain" contract.
function mapStatus(crewStatus: string): TeamMemberStatus {
  if (crewStatus === 'available' || crewStatus === 'scheduled') return 'available';
  if (crewStatus === 'on_job' || crewStatus === 'en_route')      return 'on_job';
  return 'off';
}

export function AssignModal({ job, crew, onAssign, onClose }: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dispatching, setDispatching] = useState(false);

  const members: TeamMember[] = useMemo(() => crew.map((c) => ({
    id: c.id,
    first_name: c.first_name,
    last_name: c.last_name,
    role: c.role,
    employment_type: c.employment_type,
    status: mapStatus(c.crewStatus),
    busyLabel: c.crewStatus === 'on_job' || c.crewStatus === 'en_route' ? 'BUSY' : null,
  })), [crew]);

  if (!job) return null;
  const pr = PRIORITY_STYLES[job.priority ?? 'medium'] ?? PRIORITY_STYLES.medium;

  const handleDispatch = async () => {
    if (!selectedId) return;
    setDispatching(true);
    const ok = await onAssign(job.id, selectedId);
    setDispatching(false);
    if (ok) onClose();
  };

  return (
    <SlidePanel isOpen={!!job} onClose={onClose} title="Assign Crew" subtitle={job.title}>
      {/* Job summary */}
      <div style={{ padding: '10px 12px', background: palette.g50, borderRadius: 8, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <span style={{ padding: '1px 6px', borderRadius: 3, fontSize: 9, fontWeight: 700, background: pr.bg, color: pr.fg }}>{pr.label}</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: palette.g800 }}>{job.title}</span>
        </div>
        {job.client_name && <div style={{ fontSize: 12, color: palette.g500 }}>{job.client_name}</div>}
        {(job.address || job.location) && <div style={{ fontSize: 11, color: palette.g400 }}>{job.address ?? job.location}</div>}
        {job.scheduled_date && <div style={{ fontSize: 11, color: palette.g500, marginTop: 2 }}>{job.scheduled_date} {job.scheduled_time ?? ''}</div>}
      </div>

      <div style={{ fontSize: 12, fontWeight: 600, color: palette.g600, marginBottom: 8 }}>Select crew member</div>

      <TeamMemberPicker
        members={members}
        value={selectedId}
        onChange={setSelectedId}
        defaultStatus="available"
        showSearch
      />

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 16, marginTop: 16, borderTop: `1px solid ${palette.g100}` }}>
        <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
        <button onClick={handleDispatch} disabled={!selectedId || dispatching} style={{
          padding: '8px 22px', borderRadius: 8, border: 'none',
          background: !selectedId || dispatching ? palette.g300 : palette.accent,
          color: palette.white, fontSize: 13.5, fontWeight: 600,
          cursor: !selectedId || dispatching ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
        }}>{dispatching ? 'Dispatching...' : 'Dispatch'}</button>
      </div>
    </SlidePanel>
  );
}
